import { useState } from "react";
import { InputAdornment, Stack, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

export type SizeRangePickerProps = {
  initialMin?: number | null;
  initialMax?: number | null;
  disabled?: boolean;
  /** `undefined` on either side is an open bound. Never called with 0 or NaN. */
  onRangeChange: (min: number | undefined, max: number | undefined) => void;
  /** A one-line caption under the boxes. Omit it where the surface says the same thing itself. */
  hint?: string;
  /** Focus the minimum on mount — for a popover whose only job is this control. */
  autoFocus?: boolean;
};

/** Digits only: a size is a whole number of square metres. */
const digitsOf = (raw: string): string => raw.replace(/[^\d]/g, "").slice(0, 5);

/** A typed bound → the number it means, or `undefined` for an empty box or a 0. */
export const parseSizeBound = (text: string): number | undefined => {
  const digits = digitsOf(text);
  if (!digits) return undefined;
  const value = Number(digits);
  return value > 0 ? value : undefined;
};

const textOf = (value: number | null | undefined): string =>
  typeof value === "number" && isFinite(value) && value > 0 ? String(value) : "";

/**
 * Built area (שטח בנוי) min/max, in m² — two free-typed boxes.
 *
 * NOT a stepper like rooms and floors. Those walk a short scale; a size is a number the realtor
 * already has in their head ("מעל 100 מטר"), and a stepper would either snap 95 to a neighbour or
 * need ten taps to get there. The contact dialog edits the same field the same way.
 *
 * ⚠️ NO CORRECTION WHILE TYPING. Retyping a minimum of 80 into 120 passes through "1" and "12",
 * both below a maximum of 100 — a push-the-other-bound rule would rewrite a number the realtor
 * never touched, mid-keystroke. An inverted pair is SWAPPED on blur instead: both numbers are real
 * and only their order is wrong (the rule `CellMoneyRange` and the contact dialog already follow).
 * Every keystroke is still reported, so the drawer's draft is current whenever "search" is pressed.
 */
export default function SizeRangePicker({
  initialMin,
  initialMax,
  disabled,
  onRangeChange,
  hint,
  autoFocus
}: SizeRangePickerProps) {
  const { t } = useTranslation();
  // Copied into state on mount, like the sibling pickers: the drawer owns the draft and this
  // control reports into it. A host that needs to push a NEW value in remounts it with a `key`.
  const [minText, setMinText] = useState(textOf(initialMin));
  const [maxText, setMaxText] = useState(textOf(initialMax));

  const report = (nextMin: string, nextMax: string): void => {
    onRangeChange(parseSizeBound(nextMin), parseSizeBound(nextMax));
  };

  const change = (bound: "min" | "max") => (raw: string): void => {
    const digits = digitsOf(raw);
    if (bound === "min") {
      setMinText(digits);
      report(digits, maxText);
    } else {
      setMaxText(digits);
      report(minText, digits);
    }
  };

  const normalizeOnBlur = (): void => {
    const min = parseSizeBound(minText);
    const max = parseSizeBound(maxText);
    // Only when BOTH bounds are set can they contradict — a lone bound is an open range.
    if (min !== undefined && max !== undefined && min > max) {
      setMinText(String(max));
      setMaxText(String(min));
      onRangeChange(max, min);
      return;
    }
    // A typed "0" is an empty box: show it as one, so the control never displays a bound it
    // did not report.
    if (minText && min === undefined) setMinText("");
    if (maxText && max === undefined) setMaxText("");
  };

  const unit = t("listingsMatch.sizeRangePicker.unit", { defaultValue: "מ״ר" });

  const box = (bound: "min" | "max"): JSX.Element => (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      disabled={disabled}
      autoFocus={autoFocus && bound === "min"}
      label={
        bound === "min"
          ? t("listingsMatch.sizeRangePicker.min", { defaultValue: "מ־" })
          : t("listingsMatch.sizeRangePicker.max", { defaultValue: "עד" })
      }
      value={bound === "min" ? minText : maxText}
      onChange={(event) => change(bound)(event.target.value)}
      onBlur={normalizeOnBlur}
      onFocus={(event) => event.target.select()}
      // `dir` on the INPUT: digits are LTR content even on an RTL page, and `inputMode` gets
      // phones a numeric keypad instead of the Hebrew alphabet.
      inputProps={{
        dir: "ltr",
        inputMode: "numeric",
        "aria-label": `${t("listingsMatch.filters.squareMeterTitle", {
          defaultValue: "שטח בנוי (מ״ר)"
        })} ${bound === "min" ? "min" : "max"}`
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.disabled">
              {unit}
            </Typography>
          </InputAdornment>
        )
      }}
    />
  );

  return (
    <Stack gap={0.75} sx={{ width: "100%" }}>
      <Stack direction="row" gap={1}>
        {box("min")}
        {box("max")}
      </Stack>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Stack>
  );
}
